'use strict'
/**
 * contextual-suggestions.js · P6 C.1 · sugerencias estables aurIA
 * contexto-aware según pestaña actual.
 *
 * Doctrina:
 *   · Tap en sugerencia · SE ENVÍA como mensaje (no se queda en input)
 *   · Sugerencias rotan según conversación (post-respuesta · actualizan)
 *   · Voz cumple 7 principios doc 8.4
 *   · NUNCA jerga al user
 */

// Sugerencias base por pestaña (founder cinceladura C.1)
const SUGGESTIONS_BY_PATH = {
  '/today':    [
    '¿Cómo me ha ido esta semana?',
    '¿Por qué este entreno hoy?',
    'Cambia mi plan a mañana',
  ],
  '/hub':      [
    'Analiza mi último entreno',
    'Compara con la semana pasada',
    '¿Qué dominios tocó mi última sesión?',
  ],
  '/calendar': [
    'Lista de la compra de esta semana',
    'Cambia mi cena del jueves',
    '¿Cuál es la intención de esta semana?',
  ],
  '/domains':  [
    'Explícame mi cronobiología',
    '¿Qué dominios debería priorizar?',
    '¿Cómo se conectan mis dominios?',
  ],
  '/progress': [
    'Resume mi mes',
    '¿Estoy mejorando?',
    '¿Cuál es mi dominio limitante?',
  ],
}

const DEFAULT_SUGGESTIONS = [
  '¿Cómo me ha ido esta semana?',
  '¿Qué decide el motor por mí hoy?',
  'Cambia algo de mi plan',
]

/**
 * Sugerencias post-respuesta · base generic.
 */
const FOLLOWUP_BASE = [
  'Cuéntame más',
  'Y de la nutrición · ¿qué decides?',
  'Ajusta el plan si toca',
]

/**
 * P-CLEANUP L2 mayor · inferencia mínima del contexto del último mensaje.
 * Si la última respuesta menciona temas (entreno, comida, sueño...) ·
 * sugerencias siguen ese hilo. Si no · base generic.
 */
function inferFollowupFrom(lastMessage) {
  if (!lastMessage || typeof lastMessage !== 'string') return FOLLOWUP_BASE
  const t = lastMessage.toLowerCase()
  if (/entreno|sesi[oó]n|fuerza|cardio|z2|rpe/.test(t)) {
    return ['¿Por qué este enfoque?', 'Cambia la sesión de mañana', 'Ajusta intensidad']
  }
  if (/comida|cena|desayuno|almuerzo|prote[ií]na|carbo/.test(t)) {
    return ['Cambia esta comida', 'Y el día siguiente · ¿qué comes?', '¿Por qué esta comida hoy?']
  }
  if (/sue[ñn]o|descanso|recuper/.test(t)) {
    return ['¿Cuántas horas debería dormir?', 'Ajusta la hora de acostarme', 'Cómo mejoro mi descanso']
  }
  if (/lesi[oó]n|molestia|dolor/.test(t)) {
    return ['Descansa el plan hoy', 'Qué ejercicios evito', '¿Cuándo retomo?']
  }
  return FOLLOWUP_BASE
}

// FRENTE F · 3 BLOQUES (entender / ajustar / avisar) · contexto por pantalla · acciones reales.
// La sopa de chips → 3 puertas. Cada chip es acción real (entender lee el motor, ajustar cambia
// con confirmación, avisar mete info nueva). avisar es universal (lesión/evento).
// F8 · AVISAR es universal (lesión/evento) + absorbe el "esto no me cuadra" que se retiró como
// botón flotante en F2 (FeedbackButton) → el feedback vuelve a aurIA con su contexto (ruta/día/plan).
const AVISAR = ['Tengo una molestia', 'Me ha surgido un evento', 'Esto no me cuadra']
const GROUPED_BY_PATH = {
  '/today':     { entender: ['¿Por qué este entreno hoy?', '¿Cómo voy esta semana?'], ajustar: ['Cambia mi plan a mañana', 'Sube o baja la carga de hoy'], avisar: AVISAR },
  '/hub':       { entender: ['Analiza mi último entreno', '¿Qué dominios tocó mi sesión?'], ajustar: ['Adapta el plan por viaje', 'Cambia la próxima sesión'], avisar: AVISAR },
  // F8 · /nutricion contextual (el founder lo pide: comida/plato) · antes caía a GROUPED_DEFAULT genérico
  '/nutricion': { entender: ['¿Por qué estas cifras hoy?', '¿Por qué este plato?'], ajustar: ['Cambia una comida', 'Hoy como fuera'], avisar: AVISAR },
  '/calendar':  { entender: ['¿Cuál es la intención de esta semana?'], ajustar: ['Cambia mi cena del jueves', 'Mueve una sesión'], avisar: AVISAR },
  '/domains':   { entender: ['Explícame mi cronobiología', '¿Qué dominios priorizar?'], ajustar: ['Sube o baja la carga', 'Adapta el plan por viaje'], avisar: AVISAR },
  '/progress':  { entender: ['Resume mi mes', '¿Cuál es mi dominio limitante?'], ajustar: ['Replantea mi objetivo', 'Cambia el foco del plan'], avisar: AVISAR },
}
const GROUPED_DEFAULT = { entender: ['¿Qué decide el motor por mí hoy?', '¿Cómo voy?'], ajustar: ['Cambia algo de mi plan'], avisar: AVISAR }

/** Sugerencias agrupadas en los 3 bloques · contexto por pantalla. */
export function groupedSuggestionsForPath(pathname) {
  if (!pathname) return GROUPED_DEFAULT
  if (GROUPED_BY_PATH[pathname]) return GROUPED_BY_PATH[pathname]
  for (const k of Object.keys(GROUPED_BY_PATH)) { if (pathname.startsWith(k)) return GROUPED_BY_PATH[k] }
  if (pathname.startsWith('/progreso')) return GROUPED_BY_PATH['/progress']
  if (pathname.startsWith('/dominios')) return GROUPED_BY_PATH['/domains']
  return GROUPED_DEFAULT
}

export function suggestionsForPath(pathname) {
  if (!pathname) return DEFAULT_SUGGESTIONS
  // Coincidencia exacta o prefix corto (ej /domains → /domains, /hub → /hub)
  if (SUGGESTIONS_BY_PATH[pathname]) return SUGGESTIONS_BY_PATH[pathname]
  for (const k of Object.keys(SUGGESTIONS_BY_PATH)) {
    if (pathname.startsWith(k)) return SUGGESTIONS_BY_PATH[k]
  }
  // /progreso legacy → mapea a progress
  if (pathname.startsWith('/progreso')) return SUGGESTIONS_BY_PATH['/progress']
  if (pathname.startsWith('/dominios')) return SUGGESTIONS_BY_PATH['/domains']
  return DEFAULT_SUGGESTIONS
}

export function followupSuggestions(lastMessageText) {
  return inferFollowupFrom(lastMessageText)
}

/**
 * P7 D3 · Detecta si una sugerencia es un shortcut a Facilidades
 * (abre sheet directamente sin mandar mensaje conversacional).
 *
 * Founder cinceladura D3 punto 3:
 *   · "Lista de la compra de esta semana" → SHORTCUT (1-click directo)
 *   · "Menú semanal" → SHORTCUT (1-click directo)
 *   · "Cambia mi cena del jueves" → NO shortcut · conversacional
 *     (intent parsing justifica turno de chat)
 *
 * Devuelve { type, payload } o null.
 */
export function facilityShortcutFor(text) {
  if (!text) return null
  const t = text.toLowerCase()
  if (/lista de la compra/.test(t))               return { type: 'shopping-list' }
  if (/menú semanal|menu semanal/.test(t))        return { type: 'weekly-menu' }
  if (/descargar.*día|descargar.*dia|mi día|mi dia/.test(t)) return { type: 'day-plan' }
  return null
}
